import React, { useState, useEffect } from 'react';
import { authFetch } from "../../utils/api";
import '../../styles/admin/FormularioServicio.css';

const initialState = {
  servicio: '', id_puerto: '', servicio_nombre: '',
  nota: '', 
  unidad_cobro: '', 
  tarifa_fija: '', tarifa_variable: '', tarifa_diaria: '',
  tarifa: '',
  tarifa_instalacion: '',
  // Campos para contenedores detallados
  tarifa_20_pies_lleno: '', tarifa_20_pies_vacio: '', tarifa_40_pies_lleno: '', tarifa_40_pies_vacio: '',
  
  // Estado para controlar la UI (simple o detallado)
  metodo_cobro_contenedores: 'detallado', 
  tramos_tarifa: [],
  tipo: '', tipo_de_trafico: '',
};

const FormularioServicio = ({ servicioId, onClose, onSaveSuccess }) => {
  const [formData, setFormData] = useState(initialState);
  const [puertos, setPuertos] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState(''); 

  const isEditing = servicioId != null;

  // 1. Cargar lista de puertos
  useEffect(() => {
    const fetchPuertos = async () => {
      try {
        const response = await authFetch('/api/Puerto');
        if (!response.ok) throw new Error(`Error HTTP: ${response.status}`);
        const data = await response.json();
        setPuertos(data);
      } catch (error) {
        setErrors(prev => ({ ...prev, puertos: 'No se pudieron cargar los puertos' }));
      }
    };
    fetchPuertos();
  }, []); 
  
  // 2. Cargar datos del servicio si estamos editando
  useEffect(() => {
    if (isEditing) {
      const fetchServicioData = async () => {
        setIsLoading(true);
        setFormError('');
        try {
          const response = await authFetch(`/api/Servicio/${servicioId}`);
          if (!response.ok) throw new Error('No se pudo cargar el servicio');
          const data = await response.json();
          
          data.tramos_tarifa = data.tramos_tarifa || []; 
          
          const tieneTarifasEspecificas = (
             (data.tarifa_20_pies_lleno && data.tarifa_20_pies_lleno > 0) ||
             (data.tarifa_40_pies_lleno && data.tarifa_40_pies_lleno > 0)
          );

          setFormData({ 
            ...initialState, 
            ...data,
            // Importante: El backend puede devolver UnidadCobro o unidad_cobro dependiendo de la serialización
            unidad_cobro: data.UnidadCobro || data.unidadCobro || data.unidad_cobro || '',
            metodo_cobro_contenedores: tieneTarifasEspecificas ? 'detallado' : 'simple',
          });

        } catch (error) {
          setFormError('Error al cargar datos para editar. ' + error.message);
        } finally {
          setIsLoading(false);
        }
      };
      fetchServicioData();
    } else {
      setFormData(initialState);
      setIsLoading(false);
      setFormError('');
    }
  }, [servicioId, isEditing]); 

  // 3. Manejo de cambios
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'servicio') {
      setFormData(prev => ({
        servicio: value,
        id_puerto: prev.id_puerto,
        servicio_nombre: prev.servicio_nombre,
        nota: prev.nota,
        unidad_cobro: prev.unidad_cobro, 
        ...Object.keys(initialState).reduce((acc, key) => {
          if (!['servicio', 'id_puerto', 'servicio_nombre', 'nota', 'unidad_cobro'].includes(key)) {
            acc[key] = initialState[key];
          }
          return acc;
        }, {}),
      }));
      setErrors({});
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const addTramo = () => {
    setFormData(prev => ({ 
      ...prev, 
      tramos_tarifa: [...(prev.tramos_tarifa || []), { dia_inicio: '', dia_fin: '', tarifa: '' }] 
    }));
  };

  const removeTramo = (index) => {
    setFormData(prev => ({ 
      ...prev, 
      tramos_tarifa: prev.tramos_tarifa.filter((_, i) => i !== index) 
    }));
  };

  const handleTramoChange = (index, e) => {
    const { name, value } = e.target;
    const newTramos = [...formData.tramos_tarifa];
    newTramos[index] = { ...newTramos[index], [name]: value };
    setFormData(prev => ({ ...prev, tramos_tarifa: newTramos }));
  };

  const checkIsContenerizada = () => {
    const nombre = formData.servicio_nombre ? formData.servicio_nombre.toLowerCase() : '';
    const tipo = formData.tipo ? formData.tipo.toLowerCase() : '';
    return nombre.includes('contene') || tipo.includes('contenerizada');
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.servicio) newErrors.servicio = 'El tipo de servicio es obligatorio';
    if (!formData.id_puerto) newErrors.id_puerto = 'El puerto es obligatorio';
    if (!formData.servicio_nombre) newErrors.servicio_nombre = 'El nombre descriptivo es obligatorio';
    
    // CORRECCIÓN 1: Hacemos el campo obligatorio en el frontend para evitar el error 400
    if (!formData.unidad_cobro || formData.unidad_cobro.trim() === '') {
      newErrors.unidad_cobro = 'La unidad de cobro es obligatoria (ej. Tonelada, Vehículo)';
    }

    switch (formData.servicio) {
      case 'uso_infraestructura':
        if (!formData.tarifa_fija) newErrors.tarifa_fija = 'Campo obligatorio';
        if (!formData.tarifa_variable) newErrors.tarifa_variable = 'Campo obligatorio';
        break;
      case 'muellaje':
        if (!formData.tipo) newErrors.tipo = 'El tipo es obligatorio';
        if (!formData.tarifa) newErrors.tarifa = 'La tarifa es obligatoria';
        break;
      case 'almacenaje':
        if (!formData.tipo) newErrors.tipo = 'El tipo es obligatorio';
        
        const isContenerizada = checkIsContenerizada();
        const usarTramos = !isContenerizada || (isContenerizada && formData.metodo_cobro_contenedores === 'simple');
        
        if (isContenerizada && formData.metodo_cobro_contenedores === 'detallado') {
             if (!formData.tarifa_20_pies_lleno) newErrors.tarifa_20_pies_lleno = 'Campo obligatorio';
             if (!formData.tarifa_20_pies_vacio) newErrors.tarifa_20_pies_vacio = 'Campo obligatorio';
             if (!formData.tarifa_40_pies_lleno) newErrors.tarifa_40_pies_lleno = 'Campo obligatorio';
             if (!formData.tarifa_40_pies_vacio) newErrors.tarifa_40_pies_vacio = 'Campo obligatorio';
        } 
        
        if (usarTramos) {
          if (!formData.tramos_tarifa || formData.tramos_tarifa.length === 0) {
            newErrors.tramos_tarifa = 'Debe añadir al menos un tramo de tarifa';
          }
          (formData.tramos_tarifa || []).forEach((tramo, index) => {
            if (!tramo.dia_inicio) newErrors[`tramo_inicio_${index}`] = 'Requerido';
            if (!tramo.tarifa) newErrors[`tramo_tarifa_${index}`] = 'Requerido';
          });
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!validate()) {
      return; // Eliminamos el alert molesto, ya mostramos los errores visualmente
    }

    const cleanId = isEditing && servicioId ? servicioId.toString().trim() : null;

    // CORRECCIÓN 2: Cambiamos la key 'unidad_cobro' por 'UnidadCobro'
    // Esto es crucial porque en el C# la propiedad UnidadCobro no tiene [JsonPropertyName]
    const payload = {
      servicio: formData.servicio, 
      ...(isEditing && { id: cleanId }), 
      id_puerto: formData.id_puerto,
      servicio_nombre: formData.servicio_nombre,
      nota: formData.nota || "",
      UnidadCobro: formData.unidad_cobro, // <--- CAMBIO AQUÍ: PascalCase para machear con C#
    };
    
    switch (formData.servicio) {
      case 'uso_infraestructura':
        Object.assign(payload, {
          tarifa_fija: parseFloat(formData.tarifa_fija) || 0,
          tarifa_variable: parseFloat(formData.tarifa_variable) || 0,
          tarifa_diaria: parseFloat(formData.tarifa_diaria) || 0,
          tipo: formData.tipo || "",
          tipo_de_trafico: formData.tipo_de_trafico || "",
        });
        break;
      case 'muellaje':
        Object.assign(payload, {
          tipo: formData.tipo || "",
          tarifa: parseFloat(formData.tarifa),
          tipo_de_trafico: formData.tipo_de_trafico || "",
        });
        break;
      case 'almacenaje':
        Object.assign(payload, {
          tipo: formData.tipo || "",
          tipo_de_trafico: formData.tipo_de_trafico || "",
          tarifa_instalacion: parseFloat(formData.tarifa_instalacion) || 0,
        });

        const isContenerizada = checkIsContenerizada();

        if (isContenerizada && formData.metodo_cobro_contenedores === 'detallado') {
             Object.assign(payload, {
               tarifa_20_pies_lleno: parseFloat(formData.tarifa_20_pies_lleno),
               tarifa_20_pies_vacio: parseFloat(formData.tarifa_20_pies_vacio),
               tarifa_40_pies_lleno: parseFloat(formData.tarifa_40_pies_lleno),
               tarifa_40_pies_vacio: parseFloat(formData.tarifa_40_pies_vacio),
               tramos_tarifa: [] 
             });
        } else {
             const tramosProcesados = (formData.tramos_tarifa || []).map(t => ({
               dia_inicio: parseInt(t.dia_inicio, 10),
               dia_fin: t.dia_fin ? parseInt(t.dia_fin, 10) : null,
               tarifa: parseFloat(t.tarifa),
             }));
             
             Object.assign(payload, { 
               tramos_tarifa: tramosProcesados,
               tarifa_20_pies_lleno: null,
               tarifa_20_pies_vacio: null,
               tarifa_40_pies_lleno: null,
               tarifa_40_pies_vacio: null
             });
        }
        break;
      default:
        return;
    }

    const url = isEditing ? `/api/Servicio/${servicioId}` : `/api/Servicio`;
    const method = isEditing ? 'PUT' : 'POST';

    try {
      const response = await authFetch(url, {
        method: method,
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        onSaveSuccess(); 
      } else {
        const errorText = await response.text();
        try {
            const errorJson = JSON.parse(errorText);
            // Si el error viene formateado como ProblemDetails (RFC 9110)
            if (errorJson.errors) {
              const mensajes = Object.values(errorJson.errors).flat().join(', ');
              throw new Error(mensajes);
            }
            throw new Error(errorJson.message || response.statusText);
        } catch (e) {
            throw new Error(e.message || errorText || response.statusText);
        }
      }
    } catch (error) {
      setFormError(`Error al guardar: ${error.message}`);
    }
  };

  const renderUsoInfraestructura = () => (
    <>
      <div className="form-group">
        <label htmlFor="tarifa_fija">Tarifa Fija (*)</label>
        <input type="number" step="0.01" name="tarifa_fija" id="tarifa_fija" value={formData.tarifa_fija} onChange={handleChange} />
        {errors.tarifa_fija && <div className="form-error">{errors.tarifa_fija}</div>}
      </div>
      <div className="form-group">
        <label htmlFor="tarifa_variable">Tarifa Variable (*)</label>
        <input type="number" step="0.01" name="tarifa_variable" id="tarifa_variable" value={formData.tarifa_variable} onChange={handleChange} />
        {errors.tarifa_variable && <div className="form-error">{errors.tarifa_variable}</div>}
      </div>
      <div className="form-group">
        <label htmlFor="tarifa_diaria">Tarifa Diaria (opcional)</label>
        <input type="number" step="0.01" name="tarifa_diaria" id="tarifa_diaria" value={formData.tarifa_diaria} onChange={handleChange} />
      </div>
      <div className="form-group">
        <label htmlFor="tipo">Tipo (opcional)</label>
        <input type="text" name="tipo" id="tipo" value={formData.tipo} onChange={handleChange} placeholder="Ej. tráfico de cabotaje" />
      </div>
      <div className="form-group">
        <label htmlFor="tipo_de_trafico">Tipo de Tráfico (opcional)</label>
        <input type="text" name="tipo_de_trafico" id="tipo_de_trafico" value={formData.tipo_de_trafico} onChange={handleChange} placeholder="Ej. tráfico de altura" />
      </div>
    </>
  );
  
  const renderMuellaje = () => (
     <>
      <div className="form-group">
        <label htmlFor="tipo">Tipo (*)</label>
        <input type="text" name="tipo" id="tipo" value={formData.tipo} onChange={handleChange} placeholder="Ej. Carga contenerizada, Automóvil" />
        {errors.tipo && <div className="form-error">{errors.tipo}</div>}
      </div>
      <div className="form-group">
        <label htmlFor="tarifa">Tarifa (*)</label>
        <input type="number" step="0.01" name="tarifa" id="tarifa" value={formData.tarifa} onChange={handleChange} />
        {errors.tarifa && <div className="form-error">{errors.tarifa}</div>}
      </div>
      <div className="form-group">
        <label htmlFor="tipo_de_trafico">Tipo de Tráfico (opcional)</label>
        <input type="text" name="tipo_de_trafico" id="tipo_de_trafico" value={formData.tipo_de_trafico} onChange={handleChange} />
      </div>
    </>
  );

  const renderAlmacenaje = () => {
    const isContenerizada = checkIsContenerizada();
    const mostrarTramos = !isContenerizada || (isContenerizada && formData.metodo_cobro_contenedores === 'simple');

    return (
      <>
        <div className="form-group">
          <label htmlFor="tipo">Tipo (*)</label>
          <input type="text" name="tipo" id="tipo" value={formData.tipo} onChange={handleChange} placeholder="Ej. carga contenerizada, vehículos" />
          {errors.tipo && <div className="form-error">{errors.tipo}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="tipo_de_trafico">Tipo de Tráfico (opcional)</label>
          <input type="text" name="tipo_de_trafico" id="tipo_de_trafico" value={formData.tipo_de_trafico} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="tarifa_instalacion">Tarifa de Instalación (opcional)</label>
          <input type="number" step="0.01" name="tarifa_instalacion" id="tarifa_instalacion" value={formData.tarifa_instalacion} onChange={handleChange} />
        </div>

        {isContenerizada && (
          <div className="dynamic-section sub-section">
            <h4 className="section-title">Configuración de Cobro Contenedores</h4>
            <div className="form-group" style={{ marginBottom: '15px' }}>
              <label>Método de Cobro:</label>
              <div style={{ display: 'flex', gap: '20px', marginTop: '5px' }}>
                <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'normal' }}>
                  <input 
                    type="radio" 
                    name="metodo_cobro_contenedores" 
                    value="simple" 
                    checked={formData.metodo_cobro_contenedores === 'simple'} 
                    onChange={handleChange} 
                    style={{ marginRight: '8px' }}
                  />
                  Por Cantidad (Tarifa única / Tramos)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'normal' }}>
                  <input 
                    type="radio" 
                    name="metodo_cobro_contenedores" 
                    value="detallado" 
                    checked={formData.metodo_cobro_contenedores === 'detallado'} 
                    onChange={handleChange} 
                    style={{ marginRight: '8px' }}
                  />
                  Detallado (Por tamaño/estado)
                </label>
              </div>
            </div>

            {formData.metodo_cobro_contenedores === 'detallado' && (
               <>
                <div className="form-group">
                  <label htmlFor="tarifa_20_pies_lleno">Tarifa 20' Lleno (*)</label>
                  <input type="number" step="0.01" name="tarifa_20_pies_lleno" value={formData.tarifa_20_pies_lleno} onChange={handleChange} />
                  {errors.tarifa_20_pies_lleno && <div className="form-error">{errors.tarifa_20_pies_lleno}</div>}
                </div>
                <div className="form-group">
                  <label htmlFor="tarifa_20_pies_vacio">Tarifa 20' Vacío (*)</label>
                  <input type="number" step="0.01" name="tarifa_20_pies_vacio" value={formData.tarifa_20_pies_vacio} onChange={handleChange} />
                  {errors.tarifa_20_pies_vacio && <div className="form-error">{errors.tarifa_20_pies_vacio}</div>}
                </div>
                <div className="form-group">
                  <label htmlFor="tarifa_40_pies_lleno">Tarifa 40' Lleno (*)</label>
                  <input type="number" step="0.01" name="tarifa_40_pies_lleno" value={formData.tarifa_40_pies_lleno} onChange={handleChange} />
                  {errors.tarifa_40_pies_lleno && <div className="form-error">{errors.tarifa_40_pies_lleno}</div>}
                </div>
                <div className="form-group">
                  <label htmlFor="tarifa_40_pies_vacio">Tarifa 40' Vacío (*)</label>
                  <input type="number" step="0.01" name="tarifa_40_pies_vacio" value={formData.tarifa_40_pies_vacio} onChange={handleChange} />
                  {errors.tarifa_40_pies_vacio && <div className="form-error">{errors.tarifa_40_pies_vacio}</div>}
                </div>
               </>
            )}
          </div>
        )}

        {mostrarTramos && (
          <div className="dynamic-section sub-section">
            <h4 className="section-title">
                {isContenerizada ? 'Tarifa por Cantidad (Tramos)' : 'Tramos de Tarifa por Días'}
            </h4>
            {isContenerizada && <p style={{fontSize: '0.9em', color: '#666', marginBottom: '10px'}}>Define el costo por unidad/contenedor según los días de estancia.</p>}
            
            {errors.tramos_tarifa && <div className="form-error global-error">{errors.tramos_tarifa}</div>}
            
            {(formData.tramos_tarifa || []).map((tramo, index) => (
              <div key={index} className="tramo-row">
                <div className="tramo-input">
                  <label>Día Inicio</label>
                  <input type="number" name="dia_inicio" placeholder="Ej. 1" value={tramo.dia_inicio} onChange={(e) => handleTramoChange(index, e)} />
                  {errors[`tramo_inicio_${index}`] && <div className="form-error">{errors[`tramo_inicio_${index}`]}</div>}
                </div>
                <div className="tramo-input">
                  <label>Día Fin (opc)</label>
                  <input type="number" name="dia_fin" placeholder="Ej. 10" value={tramo.dia_fin} onChange={(e) => handleTramoChange(index, e)} />
                </div>
                <div className="tramo-input">
                  <label>Tarifa</label>
                  <input type="number" step="0.01" name="tarifa" placeholder="Ej. 756.00" value={tramo.tarifa} onChange={(e) => handleTramoChange(index, e)} />
                  {errors[`tramo_tarifa_${index}`] && <div className="form-error">{errors[`tramo_tarifa_${index}`]}</div>}
                </div>
                <button type="button" onClick={() => removeTramo(index)} className="btn btn-danger" aria-label={`Eliminar tramo ${index + 1}`}>
                  &times;
                </button>
              </div>
            ))}
            <button type="button" onClick={addTramo} className="btn btn-secondary">
              + Añadir Tramo
            </button>
          </div>
        )}
      </>
    );
  };
  
  const renderDynamicFields = () => {
    switch (formData.servicio) {
      case 'uso_infraestructura': return <div className="dynamic-section">{renderUsoInfraestructura()}</div>;
      case 'muellaje': return <div className="dynamic-section">{renderMuellaje()}</div>;
      case 'almacenaje': return <div className="dynamic-section">{renderAlmacenaje()}</div>;
      default: return null;
    }
  };

  if (isLoading && isEditing) {
    return <div className="form-loading">Cargando datos del servicio...</div>;
  }
  
  return (
    <div className="aseg-main"> 
      <form className="servicio-form" onSubmit={handleSubmit} noValidate>
        <h2>{isEditing ? 'Editar Servicio' : 'Crear Nuevo Servicio'}</h2>
        
        {formError && <div className="form-error global-error">{formError}</div>}
        
        <fieldset>
          <legend>1. Campos Comunes</legend>
          <div className="form-group">
            <label htmlFor="servicio">Tipo de Servicio (*)</label>
            <select name="servicio" id="servicio" value={formData.servicio} onChange={handleChange}>
              <option value="">-- Seleccione un tipo --</option>
              <option value="uso_infraestructura">Uso de Infraestructura</option>
              <option value="almacenaje">Almacenaje</option>
              <option value="muellaje">Muellaje</option>
            </select>
            {errors.servicio && <div className="form-error">{errors.servicio}</div>}
          </div>
          <div className="form-group">
            <label htmlFor="id_puerto">Puerto (*)</label>
            <select name="id_puerto" id="id_puerto" value={formData.id_puerto} onChange={handleChange} disabled={puertos.length === 0}>
              <option value="">{puertos.length > 0 ? '-- Seleccione un puerto --' : 'Cargando...'}</option>
              {puertos.map(puerto => (
                <option key={puerto.id} value={puerto.id}>{puerto.nombre}</option>
              ))}
            </select>
            {errors.id_puerto && <div className="form-error">{errors.id_puerto}</div>}
            {errors.puertos && <div className="form-error">{errors.puertos}</div>}
          </div>
          <div className="form-group">
            <label htmlFor="servicio_nombre">Nombre Descriptivo (*)</label>
            <input type="text" name="servicio_nombre" id="servicio_nombre" value={formData.servicio_nombre} onChange={handleChange} placeholder="Ej. Almacenaje de Vehículos" />
            {errors.servicio_nombre && <div className="form-error">{errors.servicio_nombre}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="unidad_cobro">Unidad de Cobro (*)</label>
            <input 
              type="text" 
              name="unidad_cobro" 
              id="unidad_cobro" 
              value={formData.unidad_cobro} 
              onChange={handleChange} 
              placeholder="Ej. Tonelada, Vehículo, Contenedor" 
            />
            {errors.unidad_cobro && <div className="form-error">{errors.unidad_cobro}</div>}
          </div>

          <div className="form-group" style={{ width: '100%' }}>
            <label htmlFor="nota">Nota / Descripción Adicional</label>
            <textarea 
              name="nota" 
              id="nota" 
              value={formData.nota} 
              onChange={handleChange} 
              rows="3"
              style={{ width: '100%', padding: '8px', resize: 'vertical' }}
              placeholder="Ingrese cualquier detalle adicional sobre este servicio..." 
            />
          </div>
        </fieldset>

        {formData.servicio && (
          <fieldset>
            <legend>2. Tarifas del Servicio</legend>
            {renderDynamicFields()}
          </fieldset>
        )}

        <div className="form-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={!formData.servicio || isLoading}>
            {isEditing ? 'Actualizar Servicio' : 'Guardar Servicio'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormularioServicio;